import React, { FC, useEffect, useMemo, useState } from 'react'
import { BlackParagraph } from 'components/atoms/Paragraph'
import Table from 'components/organisms/Table'
import Header from 'components/organisms/Table/atoms/Header'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
  Column,
  SortingRule,
  useExpanded,
  useFlexLayout,
  useGlobalFilter,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table'
import { debounce } from 'lodash'
import { emptyArray, sortByAlphabet, sortByDate } from 'utils/array'
import { highlight } from 'utils/highlight'
import { DATE_TIME_FORMAT } from 'utils/date'
import { getDevicesFn } from 'api'
import { Page, PageHeader } from 'components/organisms/layout'
import { useUserContext } from 'contexts/UserContext'
import { Dropdown } from '../../components/molecules/Dropdown'
import { IconButton } from '../../components/molecules/Button'
import Icon from '../../components/atoms/Icon'
import { MenuItem, Popup } from '../../components/molecules/Popup'
import { LinkTo } from '../../components/atoms/Pointer'
import { MODALS } from '../../components/organisms/Modal'

interface FormattedDevice {
  i_device: number
  user: string
  deviceos: string
  clientversion: string
  created: string
  last_login: string
}

const columns: Column<FormattedDevice>[] = [
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.user')} />
    },
    accessor: 'user',
    sortType: (a, b) => sortByAlphabet(a.values.user, b.values.user),
    Cell: ({ value, state: { globalFilter } }) => (
      <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    ),
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.os')} />
    },
    accessor: 'deviceos',
    Cell: ({ value, state: { globalFilter } }) => {
      if (!value) return <div>-</div>
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
    width: 80,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.version')} />
    },
    accessor: 'clientversion',
    Cell: ({ value, state: { globalFilter } }) => {
      if (!value) return <div>-</div>
      return <BlackParagraph text={highlight(value, globalFilter.search)} size="12" />
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.created')} />
    },
    accessor: 'created',
    sortType: (a, b) => sortByDate(a.values.created, b.values.created),
    Cell: ({ value }) => {
      const date = value ? dayjs(value as string).format(DATE_TIME_FORMAT) : '-'
      return <BlackParagraph text={date} size="12" />
    },
    width: 100,
  },
  {
    Header: () => {
      const [t] = useTranslation()
      return <Header text={t('common.lastLogin')} />
    },
    accessor: 'last_login',
    sortType: (a, b) => sortByDate(a.values?.last_login, b.values?.last_login),
    Cell: ({ value }) => {
      if (!value) return <div>-</div>
      const date = dayjs(value).format(DATE_TIME_FORMAT)
      return <BlackParagraph text={date} size="12" />
    },
    width: 100,
  },
  {
    Header: () => null,
    accessor: 'i_device',
    disableSortBy: true,
    id: 'edit',
    width: 30,
    Cell: (cell) => {
      const [t] = useTranslation()
      const { i_device } = cell.row.original
      const { isSuperAdmin } = useUserContext()

      return isSuperAdmin ? (
        <Dropdown
          button={
            <IconButton>
              <Icon icon="uil:ellipsis-v" />
            </IconButton>
          }
        >
          <Popup>
            <LinkTo to={`/devices/${i_device}?modal=${MODALS.editDevice}`}>
              <MenuItem value="edit-admin" icon={<Icon icon="uil:edit" />} onClick={() => {}}>
                {t('common.edit')}
              </MenuItem>
            </LinkTo>
          </Popup>
        </Dropdown>
      ) : null
    },
  },
]

const initialGlobalFilter = { feature_profile: [], search: '' }

const Devices: FC = () => {
  const [t] = useTranslation()
  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const [pagination, setPagination] = useState(() => ({ pageIndex: 0, pageSize: 50 }))
  const [sortBy, setSortBy] = useState<SortingRule<FormattedDevice>[]>(() => [{ id: 'last_login', desc: true }])
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const { data: devices, isLoading } = useQuery(['devices', i_realm, pagination, sortBy[0], search], getDevicesFn, {
    select: (data) => {
      const formattedData = data.items.map(
        ({ i_device, clientversion, deviceos, customer: { user, created }, session }) => ({
          i_device,
          user: user?.user_id || '-',
          deviceos,
          clientversion,
          created,
          last_login: session?.last_login,
        })
      ) as FormattedDevice[]

      return { items: formattedData, total: data.total }
    },
  })

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        tableProps.setGlobalFilter({
          ...tableProps.state.globalFilter,
          search: value,
        })
        setSearch(value)
      }, 300),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    return () => debouncedSetSearch.cancel()
  }, [debouncedSetSearch])

  const pageCount = useMemo(
    () => Math.ceil(((devices?.total as number) || 0) / pagination.pageSize) || 0,
    [devices?.total, pagination.pageSize]
  )

  const tableProps = useTable(
    {
      columns,
      data: devices?.items ?? emptyArray,
      initialState: { pageSize: pagination.pageSize, globalFilter: { ...initialGlobalFilter } },
      autoResetPage: false,
      disableSortRemove: true,
      manualPagination: true,
      manualSortBy: true,
      manualGlobalFilter: true,
      matablePropsnualGlobalFilter: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useFlexLayout,
    useResizeColumns
  )

  useEffect(() => {
    const { pageIndex, pageSize, sortBy: _sortBy } = tableProps.state
    if (pageIndex !== pagination.pageIndex || pageSize !== pagination.pageSize) {
      setPagination({ pageIndex, pageSize })
    }

    if (_sortBy[0]) {
      const item = _sortBy[0]
      const currentItem = sortBy[0]
      if (!currentItem || item.id !== currentItem?.id || item?.desc !== currentItem?.desc) {
        setSortBy([item])
      }
    }
  }, [tableProps.state, pagination, sortBy])

  return (
    <Page title={t('page.devices.title')}>
      <PageHeader
        title={t('page.devices.title')}
        subtitle={devices?.total || ''}
        search={searchInput}
        onClearFilters={() => {
          setSearchInput('')
          setSearch('')
        }}
        onChangeSearch={(query) => {
          setSearchInput(query)
          debouncedSetSearch(query)
        }}
      />
      <Table
        isLoading={isLoading}
        {...tableProps}
        setGlobalFilter={() => {
          setSearchInput('')
          setSearch('')
        }}
      />
    </Page>
  )
}

export default Devices
